import React from 'react';
import {
  Action, ActionProps, ConfirmModal, ICON_CHECK, Portal, useModal, useNotifications,
} from '@doveit/bricks';
import { formatEuro } from '@doveit/hammer';
import { Agent, ManagerGoal } from '../../../providers/api/dtos';
import { createManagerGoal } from '../../../providers/api/goal/goalProvider';

interface ChildFnProps {
  acceptManagerGoal: VoidFunction,
  isSaving: boolean,
}

export interface AcceptManagerGoalActionProps extends Pick<ActionProps, 'disabled' | 'loading'>, React.AriaAttributes {
  agentId: Agent['id'],
  target: number,
  onSuccess?: (goal: ManagerGoal) => void,
  children?: (childFnProps: ChildFnProps) => React.ReactNode,
}

export const ACCEPT_MANAGER_GOAL_SUCCESS_MESSAGE = 'Obiettivo di fatturato salvato correttamente.';
export const ACCEPT_MANAGER_GOAL_ERROR_MESSAGE = 'Non è stato possibile salvare l\'obiettivo di fatturato.';

export const AcceptManagerGoalAction: React.FC<AcceptManagerGoalActionProps> = ({
  agentId,
  target,
  loading,
  disabled,
  onSuccess,
  children,
  ...rest
}) => {
  const [isSaving, setIsSaving] = React.useState(false);
  const { addSuccess, addError } = useNotifications();
  const modal = useModal();

  const acceptManagerGoal = React.useCallback(async () => {
    setIsSaving(true);

    try {
      const goal = await createManagerGoal({ agentId, target });
      modal.close();

      addSuccess(ACCEPT_MANAGER_GOAL_SUCCESS_MESSAGE);
      onSuccess?.(goal);
    } catch (error: unknown) {
      addError(ACCEPT_MANAGER_GOAL_ERROR_MESSAGE);
      return;
    } finally {
      setIsSaving(false);
    }
  }, [agentId, target, modal, addSuccess, onSuccess, addError]);

  return (
    <>
      {
        children
          ? children({
            acceptManagerGoal: () => modal.open(),
            isSaving,
          })
          : (
            <Action
              label="Accetta obiettivo"
              aria-label="Pulsante per accettare l'obiettivo di fatturato"
              color="primary"
              emphasis="high"
              iconLeft={{ path: ICON_CHECK }}
              loading={loading || isSaving}
              disabled={disabled}
              onClick={() => modal.open()}
              {...rest}
            />
          )
      }
      <Portal>
        <ConfirmModal
          isOpen={modal.isOpen}
          title="Accetta l'obiettivo di fatturato"
          aria-label="Accetta l'obiettivo di fatturato"
          onConfirm={acceptManagerGoal}
          onAbort={modal.close}
        >
          Accettando l&apos;obiettivo di fatturato di {formatEuro(target)} potrai monitorare le performance del team rispetto alle metriche stabilite. L&apos;obiettivo potrà essere modificato successivamente.
        </ConfirmModal>
      </Portal>
    </>
  );
};

export default AcceptManagerGoalAction;
